import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { NetworkStatus, useQuery } from '@apollo/client';
import LogRocket from 'logrocket';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { NavLink, Redirect, useRouteMatch } from 'react-router-dom';
import { useQueryState } from 'react-router-use-location-state';
import { useFeatureFlag, usePermissions, } from '@marvelapp/ballpark-application';
import { Badge, BtwButton, BtwLink, BtwModal, Button, CtaMessage, Heading, NotFound, Stack, Text, } from '@marvelapp/ballpark-components';
import { FlagIcon, LoaderIcon } from '@marvelapp/ballpark-icons';
import { formatDateAndTime } from '@marvelapp/core';
import styled from '@marvelapp/styled';
import { Box, Loader } from '@marvelapp/ui';
import { getMainScreenerSteps, getOrderedListOfAllSteps, } from '@marvelapp/user-test-creator';
import { isRecordingProcessing } from '../../components/BallparkMediaPlayer';
import { SidebarPane } from '../../components/SidebarPane';
import { ResponseStatus } from '../../graphql/graphqlTypes';
import { GET_USER_TEST_INFO } from '../../graphql/queries';
import { getPageError, getPageErrorCopy } from '../../utils/getPageErrorCopy';
import { ReportResponse as ReportResponseModal, } from '../NewResponses/IndividualResultActions/ReportResponse';
import { useGetOrderForResponse } from '../NewResponses/IndividualResultActions/useGetOrderForResponse';
import { DeleteResult } from './DeleteResult';
import Divider from './Divider';
import ResponseInfo from './ResponseInfo';
import SidebarRecordingStatus from './SidebarRecordingStatus';
import { SlideCards } from './SlideCards';
import { SlidesList } from './SlidesList';
import { updateResultStatus, useQueryUserTestResult, } from './useQueryUserTestResult';
const ACTIVE_SLIDE_QUERY_PARAM = 's'; // keep it short as no one reads urls
export const Response = observer(function Response() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const hasStaticUi = useFeatureFlag('marvel-3-static-ui');
    const permissions = usePermissions();
    const canEdit = permissions.canEditUserTestResults();
    const match = useRouteMatch();
    const { testId, responseId } = match.params;
    const [activeSlideUUID, setActiveSlideUUID] = useQueryState(ACTIVE_SLIDE_QUERY_PARAM, '');
    const { data: projectData, loading: isUserTestLoading, error, } = useQuery(GET_USER_TEST_INFO, {
        variables: { pk: Number(testId) },
    });
    const userTestResultQueryVariables = {
        pk: Number(responseId),
        uuid: ((_a = projectData === null || projectData === void 0 ? void 0 : projectData.userTest) === null || _a === void 0 ? void 0 : _a.uuid) || '',
        revision: 0,
    };
    const { resultData, isResultLoading, networkStatus, refetch } = useQueryUserTestResult({
        variables: userTestResultQueryVariables,
        skip: !((_b = projectData === null || projectData === void 0 ? void 0 : projectData.userTest) === null || _b === void 0 ? void 0 : _b.uuid),
    });
    const userTestDoc = resultData &&
        resultData.userTestDocument.document &&
        JSON.parse(resultData.userTestDocument.document);
    const isLoadingData = (isResultLoading && networkStatus === NetworkStatus.loading) ||
        isUserTestLoading;
    const testName = (_d = (_c = projectData === null || projectData === void 0 ? void 0 : projectData.userTest) === null || _c === void 0 ? void 0 : _c.prototype) === null || _d === void 0 ? void 0 : _d.name;
    const steps = useMemo(() => getAllSteps(userTestDoc), [userTestDoc]);
    if (isLoadingData) {
        return (_jsx(Page, { children: _jsx(LoaderWrapper, { children: _jsx(Loader, {}) }) }));
    }
    const pageError = getPageError({
        data: projectData,
        loading: isUserTestLoading,
        error,
    });
    if (pageError) {
        return _jsx(NotFound, Object.assign({}, getPageErrorCopy(pageError)));
    }
    if (!resultData) {
        return (_jsx(NotFound, { heading: "Result not found", subheading: "We can't find a result with this id. Please contact the test owner or account admin for more info.", testId: "project-not-found-title" }));
    }
    if ((_f = (_e = projectData === null || projectData === void 0 ? void 0 : projectData.userTest) === null || _e === void 0 ? void 0 : _e.prototype) === null || _f === void 0 ? void 0 : _f.isLocked) {
        return _jsx(Redirect, { to: `/projects/team` });
    }
    const { userTestResult } = resultData;
    if (userTestResult.status === 'REJECTED') {
        return (_jsx(Stack, { align: "center", className: "min-h-screen bg-gray-50 px-8 py-6", "data-testid": "response-reported-message", justify: "center", children: _jsx(CtaMessage.Root, { children: _jsxs(CtaMessage.Header, { icon: _jsx(FlagIcon, {}), children: [_jsx(CtaMessage.Heading, { children: "Thank you for reporting this response" }), _jsx(CtaMessage.Description, { className: "max-w-[600px]", children: "We've deleted the response. You can now close this tab." })] }) }) }));
    }
    const stepResponses = userTestResult.stepResponses.reduce((acc, response) => {
        const stepUUID = response.stepUuid;
        // eslint-disable-next-line no-param-reassign
        acc[stepUUID] = response;
        return acc;
    }, {});
    const { bounceStep, status } = userTestResult;
    const bounceStepIndex = getBounceStepIndex({
        userTest: userTestDoc,
        bounceStep,
        steps,
        stepResponses,
    });
    const hasScreenerSteps = !!userTestDoc.screeners;
    return (_jsxs(Page, { children: [_jsx(LogrocketTrackScreenersResponse, { hasScreenerSteps: hasScreenerSteps }), _jsx(SidebarPane, { children: _jsx(Scrollbars, { autoHide: true, children: _jsxs(SidebarInner, { children: [_jsxs(SidebarHeader, { "data-testid": "response-info", children: [_jsxs(Stack, { direction: "row", gap: "2", className: "px-2 py-2", wrap: "wrap", children: [_jsxs(Heading, { testId: "user-id", size: "l", truncated: true, title: userTestResult.participant.anonymousId, children: ["User # ", userTestResult.participant.anonymousId] }), _jsx(ScreeningStatus, { hasScreenerSteps: hasScreenerSteps, status: userTestResult.status })] }), _jsxs(Text, { size: "s", paddingX: "xs", children: ["Response in", ' ', _jsx(BtwLink, { asChild: true, className: "inline", variant: "current", children: _jsx(NavLink, { to: `/project/${testId}/responses`, children: testName }) }), _jsx("br", {}), "Taken on ", getSubmissionDate(userTestResult.submittedAt)] }), _jsx(Stack, { className: "mt-3 px-2", width: "full", children: userTestResult.status === 'IN_PROGRESS' ? (_jsx(Badge, { "data-testid": "response-in-progress", leadingIcon: _jsx(LoaderIcon, {}), children: "Still in progress" })) : (isRecordingProcessing((_h = (_g = userTestResult.recordings) === null || _g === void 0 ? void 0 : _g.download) === null || _h === void 0 ? void 0 : _h.uploadStatus) && (_jsx(Badge, { "data-testid": "recording-processing-in-progress", leadingIcon: _jsx(LoaderIcon, {}), children: "Processing video" }))) }), _jsxs(SidebarButtons, { children: [hasStaticUi && (_jsx(Button, { marginRight: "xs", size: "s", children: "Share" })), canEdit && (_jsxs(Stack, { align: "center", direction: "row", gap: "2", children: [_jsx(ReportResponse, { responseUUID: userTestResult.responseUUID, userTestResultQueryVariables: userTestResultQueryVariables }), _jsx(DeleteResult, {})] }))] }), _jsx(SidebarRecordingStatus, { userTestResult: userTestResult }), _jsx(Divider, {}), _jsx(ResponseInfo, { device: userTestResult.device, participant: userTestResult.participant }), _jsx(Divider, { mb: 0 })] }), _jsx(Stack, { align: "stretch", flex: "1", justify: "stretch", width: "full", children: _jsx(SlideCards, { activeSlideUUID: activeSlideUUID, bounceStepIndex: bounceStepIndex, resultStatus: userTestResult.status, setActiveSlideUUID: setActiveSlideUUID, userTest: userTestDoc }) })] }) }) }), _jsx(SlidesList, { activeSlideUUID: activeSlideUUID, bounceStepIndex: bounceStepIndex, resultStatus: status, stepResponses: stepResponses, steps: steps, userTest: userTestDoc, userTestResult: userTestResult, pusherProjectPk: (_k = (_j = projectData === null || projectData === void 0 ? void 0 : projectData.userTest) === null || _j === void 0 ? void 0 : _j.prototype) === null || _k === void 0 ? void 0 : _k.pk, refetch: refetch })] }));
});
const SCREENING_QUALIFIED = [
    ResponseStatus.Incomplete,
    ResponseStatus.Completed,
    ResponseStatus.QualifiedViewed,
];
const SCREENING_STATUSES = [
    ...SCREENING_QUALIFIED,
    ResponseStatus.ScreeningAbandoned,
    ResponseStatus.ScreeningDeclined,
];
const ScreeningStatus = observer(function ScreeningStatus({ hasScreenerSteps, status, }) {
    if (!hasScreenerSteps || !SCREENING_STATUSES.includes(status))
        return null;
    let screeningStatus = '';
    let badgeVariant;
    if (SCREENING_QUALIFIED.includes(status)) {
        screeningStatus = 'Qualified';
        badgeVariant = 'green';
    }
    if (status === ResponseStatus.ScreeningDeclined) {
        screeningStatus = 'Disqualified (Screened Out)';
        badgeVariant = 'red';
    }
    if (status === ResponseStatus.ScreeningAbandoned) {
        screeningStatus = 'Disqualified (Abandoned)';
        badgeVariant = 'red';
    }
    if (!screeningStatus || !badgeVariant)
        return null;
    return (_jsx(Badge, { "data-testid": "screening-status", size: "sm", variant: badgeVariant, children: screeningStatus }));
});
const ReportResponse = observer(function ReportResponse({ responseUUID, userTestResultQueryVariables, }) {
    const match = useRouteMatch();
    const { responseId } = match.params;
    const { getOrderForResponse, isLoadingOrder, order } = useGetOrderForResponse(responseUUID);
    const [modalView, setModalView] = React.useState(null);
    const openReportResponses = useCallback(() => {
        getOrderForResponse();
        setModalView('REPORT_RESPONSE');
    }, [getOrderForResponse]);
    const closeModal = useCallback((shouldOpen) => {
        if (!shouldOpen)
            setModalView(null);
    }, []);
    return (_jsxs(BtwModal.Root, { open: modalView !== null, onOpenChange: closeModal, children: [_jsx(BtwModal.Trigger, { asChild: true, children: _jsx(BtwButton, { "data-testid": "report-result-cta", leadingIcon: _jsx(FlagIcon, {}), onClick: openReportResponses, size: "sm", children: "Report" }) }), _jsx(BtwModal.Content, { "data-testid": "report-response-modal", hasPortal: true, children: _jsx(ReportResponseModal, { isLoadingOrder: isLoadingOrder, modalView: modalView, order: order, onSuccess: (cache) => {
                        updateResultStatus(cache, ResponseStatus.Rejected, userTestResultQueryVariables);
                    }, resultPk: Number(responseId), resultUUID: responseUUID, setModalView: setModalView }) })] }));
});
function getAllSteps(userTestDoc) {
    if (!userTestDoc)
        return [];
    const mainTestSteps = getOrderedListOfAllSteps({ userTest: userTestDoc });
    let allSteps = mainTestSteps;
    if (userTestDoc.screeners) {
        const screenerSteps = getMainScreenerSteps(userTestDoc);
        allSteps = [...screenerSteps, ...mainTestSteps];
    }
    return allSteps;
}
function getBounceStepIndex({ bounceStep, stepResponses, steps, userTest, }) {
    if (!bounceStep)
        return null;
    let bounceStepUuid = bounceStep;
    const { screeners } = userTest;
    // if the decline step is the last step the user has seen, set the last answered
    // screener step as the bounce step
    if (screeners && bounceStepUuid === screeners.decline.uuid) {
        screeners.steps.forEach((stepUUID) => {
            if (stepResponses[stepUUID].stepSkipped === false)
                bounceStepUuid = stepUUID;
        });
    }
    return steps.findIndex((step) => step.uuid === bounceStepUuid);
}
function getSubmissionDate(dateString) {
    const { date, time } = formatDateAndTime(new Date(dateString));
    return `${date}, ${time}`;
}
const Page = styled(Box) `
  display: flex;
  height: 100vh;
  justify-content: stretch;
`;
const SidebarInner = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  min-height: 100%;
`;
const SidebarHeader = styled(Box).attrs({
    paddingX: 's',
    paddingTop: 's',
    bg: 'background',
    zIndex: 'record.controls',
}) `
  position: sticky;
  top: 0;
  left: 0;
`;
const SidebarButtons = styled(Box).attrs({
    marginTop: 's',
    paddingX: 'xs',
}) `
  display: flex;
  align-items: center;
`;
const LoaderWrapper = styled.div `
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
const LogrocketTrackScreenersResponse = observer(function LogrocketTrackConditionalLogic({ hasScreenerSteps, }) {
    useEffect(() => {
        var _a;
        if (hasScreenerSteps && ((_a = process.env.DEPLOY_ENV) === null || _a === void 0 ? void 0 : _a.includes('production'))) {
            LogRocket.track('screeners-response');
        }
    }, [hasScreenerSteps]);
    return null;
});
